import React, { useState, useRef, useEffect } from "react"

import { isNumber } from "../../utils"

export default function AbstractGraphSelectionArea({
  x,
  y,
  y2,
  margin = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  fullY,
  onSelect,
  selecting = false,
  canSelectX = false,
  canSelectY = false,
  color = "var(--lanzalake-green-primary)",
  label = "",
}) {
  const [_dummyX, width] = x.range(),
    [height, _dummyY] = y.range()

  const [selectionParameters, setSelectionParameters] = useState({
    originX: undefined,
    originY: undefined,
    targetX: undefined,
    targetY: undefined,
  })

  const updateSelectionParameters = (obj) => {
    setSelectionParameters((prevState) => ({
      ...prevState,
      ...obj,
    }))
  }

  const xChanging = () => {
    const { originX, targetX } = selectionParameters
    if (originX === undefined) return false

    return (originX !== 0 && originX !== width) || (targetX !== 0 && targetX !== width)
  }

  const yChanging = () => {
    const { originY, targetY } = selectionParameters
    if (originY === undefined) return false
    if (fullY) return false

    return (originY !== 0 && originY !== width) || (targetY !== 0 && targetY !== height)
  }

  const clickCatcher = useRef()

  const target = (ev) => {
    if (clickCatcher.current) {
      const catcherBox = clickCatcher.current.getBoundingClientRect()
      return [
        Math.max(0, Math.min(ev.clientX - catcherBox.x - margin.left, width)),
        Math.max(0, Math.min(ev.clientY - catcherBox.y - margin.top, height)),
      ]
    }
    return []
  }

  const downHandler = (ev) => {
    const [targetX, targetY] = target(ev)
    updateSelectionParameters({
      originX: targetX,
      originY: targetY,
      targetX: targetX,
      targetY: targetY,
    })
  }

  useEffect(() => {
    const rootNode = document.getElementById("application-page")

    const upHandler = (ev) => {
      const { originX, originY } = selectionParameters
      const [targetX, targetY] = target(ev)

      const minX = Math.min(originX, targetX),
        minY = Math.min(originY, targetY),
        maxX = Math.max(originX, targetX, minX + 1),
        maxY = Math.max(originY, targetY, minY + 1),
        minXAxis = x && x.invert(minX),
        maxXAxis = x && x.invert(maxX),
        minYAxis = y && y.invert(maxY),
        maxYAxis = y && y.invert(minY),
        minY2Axis = y2 && y2.invert(maxY),
        maxY2Axis = y2 && y2.invert(minY)

      onSelect(
        xChanging() && isNumber(minXAxis) && isNumber(maxXAxis),
        yChanging() && isNumber(minYAxis) && isNumber(maxYAxis),
        yChanging() && isNumber(minY2Axis) && isNumber(maxY2Axis),
        minXAxis,
        maxXAxis,
        minYAxis,
        maxYAxis,
        minY2Axis,
        maxY2Axis,
      )

      updateSelectionParameters({
        originX: undefined,
        originY: undefined,
        targetX: undefined,
        targetY: undefined,
      })
    }

    const moveHandler = (ev) => {
      const [targetX, targetY] = target(ev)

      updateSelectionParameters({
        targetX: targetX,
        targetY: targetY,
      })
    }

    rootNode.addEventListener("mousemove", moveHandler, false)
    rootNode.addEventListener("mouseup", upHandler, false)
    return () => {
      rootNode.removeEventListener("mousemove", moveHandler, false)
      rootNode.removeEventListener("mouseup", upHandler, false)
    }
  }, [selectionParameters])

  const { originX, targetX, originY, targetY } = selectionParameters

  let boxMinX, boxMinY, boxMaxX, boxMaxY, boxHalfX, boxHalfY
  if (originX !== undefined) {
    boxMinX = !canSelectX ? 0 : Math.min(originX, targetX)
    boxMaxX = !canSelectX ? width : Math.max(boxMinX + 1, targetX, originX)
    boxMinY = !canSelectY ? 0 : Math.min(originY, targetY)
    boxMaxY = !canSelectY ? height : Math.max(boxMinY + 1, targetY, originY)

    boxHalfX = (boxMinX + boxMaxX) / 2
    boxHalfY = (boxMinY + boxMaxY) / 2
  }

  return (
    <>
      <rect
        x={0}
        y={0}
        width={width + margin.left + margin.right}
        height={height + margin.top + margin.bottom}
        fill="white"
        opacity={0.01}
        onMouseDown={downHandler}
        cursor={selecting ? "crosshair" : null}
        pointerEvents={selecting ? null : "none"}
        ref={clickCatcher}
      />
      <g transform={`translate(${margin.left}, ${margin.top})`}>
        {selecting && originX !== undefined && (
          <rect
            className="zoom-box"
            x={boxMinX}
            y={boxMinY}
            width={boxMaxX - boxMinX}
            height={boxMaxY - boxMinY}
            fill={color}
            fillOpacity="0.25"
            stroke={color}
          />
        )}
        {canSelectX && originX && (
          <>
            <path
              d={`M${boxMinX},${boxHalfY}
                  L${boxMaxX},${boxHalfY}
                  M${boxMinX + 6}, ${boxHalfY - 8}
                  L${boxMinX}, ${boxHalfY}
                  L${boxMinX + 6}, ${boxHalfY + 8}
                  M${boxMaxX - 6}, ${boxHalfY - 8}
                  L${boxMaxX}, ${boxHalfY}
                  L${boxMaxX - 6}, ${boxHalfY + 8}`}
              fill="none"
              stroke={color}
            />
            {label && (
              <text
                x={boxHalfX}
                y={boxHalfY + 5}
                dominantBaseline="hanging"
                textAnchor="middle"
                fill="black"
                fontFamily="sans-serif"
                fontSize="14"
              >
                {label}
              </text>
            )}
          </>
        )}
        {canSelectY && originY && (
          <path
            d={`M${boxHalfX},${boxMinY}
                  L${boxHalfX},${boxMaxY}
                  M${boxHalfX - 8},${boxMinY + 6}
                  L${boxHalfX},${boxMinY}
                  L${boxHalfX + 8},${boxMinY + 6}
                  M${boxHalfX - 8},${boxMaxY - 6}
                  L${boxHalfX},${boxMaxY}
                  L${boxHalfX + 8},${boxMaxY - 6}`}
            fill="none"
            stroke={color}
          />
        )}
      </g>
    </>
  )
}
